import { action } from 'mobx'
import { func, type IValidationSchema, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import {
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { type RynoType, Types } from '@/features/legends/editor/ryno-types'
import { RynoEditorViewModel } from '@/features/legends/ryno/RynoEditorViewModel'
import { when } from '@/utils/validx-util'
import { ConfigEditorViewModelBase } from './ConfigEditorViewModelBase'

/**
 * Options for {@link FormulaConfigEditorViewModel}.
 */
export interface FormulaConfigOptions<K extends string> {
  /**
   * The key the config has the formula under.
   */
  key: K
  neededType: RynoType
  /**
   * Whether the formula is validated; always by default.
   */
  isValidated?: () => boolean
}

/**
 * A config that is a single formula under one key, like the title config's
 * `expr`. Its errors show under `formula`, whatever the key.
 */
export class FormulaConfigEditorViewModel<
  K extends string,
> extends ConfigEditorViewModelBase<Partial<Record<K, string>>> {
  private readonly _key: K
  private readonly _isValidated: () => boolean
  private readonly _schema: IValidationSchema<FormulaConfigEditorViewModel<K>> =
    {
      formula: [
        when(
          () => this._isValidated(),
          func(() => this.formula.analyze(), 'There are formula errors.'),
        ),
      ],
    }

  readonly formula: RynoEditorViewModel

  readonly validation = validationContext(this, this._schema)

  constructor(
    dependencies: LegendPartDependencies,
    { key, neededType, isValidated = () => true }: FormulaConfigOptions<K>,
  ) {
    super(dependencies)
    this._key = key
    this._isValidated = isValidated
    this.formula = new RynoEditorViewModel({
      getSymbolTable: () => this._symbolTable,
      getNeededType: () => neededType,
    })
    this.lifecycle.manage(this.formula)
  }

  /** @inheritDoc */
  @action
  hydrate(config: Partial<Record<K, string>> | undefined) {
    this.formula.setValue(config?.[this._key])
  }

  /** @inheritDoc */
  receiveErrors(descriptor: ErrorDescriptor) {
    const { [this._key]: formula, ...inner } = descriptor.inner ?? {}
    receiveErrors(this, { ...inner, formula })
  }

  /** @inheritDoc */
  toJSON() {
    const json: Partial<Record<K, string>> = {}
    json[this._key] = this.formula.toJSON()
    return json
  }
}

/**
 * How the request title is computed.
 *
 * @param dependencies
 */
export function createTitleConfig(dependencies: LegendPartDependencies) {
  return new FormulaConfigEditorViewModel(dependencies, {
    key: 'expr',
    neededType: Types.string,
  })
}

/**
 * The request's specifications, a list of strings.
 *
 * @param dependencies
 */
export function createSpecsConfig(dependencies: LegendPartDependencies) {
  return new FormulaConfigEditorViewModel(dependencies, {
    key: 'expr',
    neededType: Types.list(Types.string),
  })
}

/**
 * Whether the job is eligible for the Tax Plan integration. The formula is
 * only validated while the integration is enabled, since the config is hidden
 * otherwise.
 *
 * @param dependencies
 * @param isEnabled
 */
export function createTaxPlanConfig(
  dependencies: LegendPartDependencies,
  isEnabled: () => boolean,
) {
  return new FormulaConfigEditorViewModel(dependencies, {
    key: 'integrationEnabledExpr',
    neededType: Types.boolean,
    isValidated: isEnabled,
  })
}

/**
 * Whether the job uses the personal tax organizer. The organizer only applies
 * to tax legends, so the formula is only validated for those.
 *
 * @param dependencies
 * @param isTaxLegend
 */
export function createPersonalOrganizerConfig(
  dependencies: LegendPartDependencies,
  isTaxLegend: () => boolean,
) {
  return new FormulaConfigEditorViewModel(dependencies, {
    key: 'enabledExpr',
    neededType: Types.boolean,
    isValidated: isTaxLegend,
  })
}
